import type { PeriodNamingMapping } from '@/features/autoIso/types/GameTypeMetadataModel';
import type { PlaybackRange } from '@/shared/types/GameStateModel';
import { decodePeriodDisplay } from '@/shared/utils/getPeriodName';

export type TimelineMapping = {
  timestamp: number;
  period?: number;
  gameTime?: string;
};

export type TimelinePeriodRange = {
  code: number;
  start: number;
  end: number;
};

const isQuarterLeague = (periodNamingMapping?: PeriodNamingMapping): boolean =>
  periodNamingMapping?.periodName?.toLowerCase() === 'quarter';

const isPeriodLeague = (periodNamingMapping?: PeriodNamingMapping): boolean => {
  const name = periodNamingMapping?.periodName?.toLowerCase();
  return name === undefined || name === 'period';
};

const isZeroGameTime = (gameTime?: string): boolean => {
  if (!gameTime) return false;

  const numericTime = Number(gameTime);
  if (Number.isFinite(numericTime)) return numericTime === 0;

  const match = /^(\d+):(\d{2})(?:\.\d+)?$/.exec(gameTime);
  return !!match && Number(match[1]) === 0 && Number(match[2]) === 0;
};

const getSegmentKey = (code: number, periodNamingMapping?: PeriodNamingMapping, seasonType?: string | null): number | null => {
  const display = decodePeriodDisplay(code, periodNamingMapping, seasonType);
  if (display.type === 'pregame') return 100;
  if (display.type === 'period') return code;
  if (display.type === 'interval') return 300;
  if (display.type === 'intermission') return code;
  if (display.type === 'overtime') return code;
  if (display.type === 'shootout') return code;
  return null;
};

/**
 * Keeps a seek inside the footage window and outside every player-reported
 * unavailable range. Ranges are [start, end), so the left candidate is one
 * millisecond before the inclusive start while an exact end remains playable.
 */
export const snapToPlayablePosition = (
  seconds: number,
  timelineStartMs: number,
  timelineDurationSeconds: number,
  unavailableRanges: PlaybackRange[],
): number | null => {
  if (!Number.isFinite(timelineStartMs) || timelineDurationSeconds <= 0) return null;

  const timelineEndMs = timelineStartMs + timelineDurationSeconds * 1000;
  const clampedSeconds = Math.min(Math.max(seconds, 0), timelineDurationSeconds);
  const targetMs = timelineStartMs + clampedSeconds * 1000;
  const range = unavailableRanges.find(
    (candidate) => targetMs >= candidate.start / 1_000_000 && targetMs < candidate.end / 1_000_000,
  );
  if (!range) return clampedSeconds;

  const rangeStartMs = range.start / 1_000_000;
  const rangeEndMs = range.end / 1_000_000;
  const candidates: number[] = [];

  if (rangeStartMs > timelineStartMs) {
    candidates.push((Math.min(rangeStartMs - 1, timelineEndMs) - timelineStartMs) / 1000);
  }
  if (rangeEndMs <= timelineEndMs) {
    candidates.push((rangeEndMs - timelineStartMs) / 1000);
  }

  const isUnavailable = (candidateSeconds: number) => {
    const candidateMs = timelineStartMs + candidateSeconds * 1000;
    return unavailableRanges.some(
      (candidate) => candidateMs >= candidate.start / 1_000_000 && candidateMs < candidate.end / 1_000_000,
    );
  };
  const playableCandidates = candidates.filter((candidate) => !isUnavailable(candidate));
  if (playableCandidates.length === 0) return null;

  return playableCandidates.reduce((nearest, candidate) =>
    Math.abs(seconds - candidate) < Math.abs(seconds - nearest) ? candidate : nearest,
  );
};

/**
 * Builds phase annotations without inventing postgame phases.
 *
 * A basketball game's fourth-quarter clock reaching 00:00 closes Q4. The
 * player can continue recording after SES mappings stop, but that terminal
 * marker must not start or preserve another Q4 annotation. Hockey commonly
 * reports a final Intermission 3 after P3; that is postgame footage unless a
 * later overtime/shootout mapping proves play resumed.
 *
 * Mapping cadence is deliberately irrelevant. A single interval record is
 * valid until the next actual phase transition, so sparse scoreboard updates
 * must never create timeline divisions.
 */
export const buildTimelinePeriodRanges = (
  mappings: TimelineMapping[],
  periodNamingMapping?: PeriodNamingMapping,
  seasonType?: string | null,
): TimelinePeriodRange[] => {
  const records = [...mappings].sort((a, b) => a.timestamp - b.timestamp);
  const laterExtraPeriod = new Array<boolean>(records.length).fill(false);
  let extraPeriodAhead = false;
  for (let index = records.length - 1; index >= 0; index -= 1) {
    laterExtraPeriod[index] = extraPeriodAhead;
    const period = records[index].period;
    if (period === undefined) continue;

    const type = decodePeriodDisplay(period, periodNamingMapping, seasonType).type;
    if (type === 'overtime' || type === 'shootout') extraPeriodAhead = true;
  }
  const ranges: TimelinePeriodRange[] = [];
  let activeRange: TimelinePeriodRange | undefined;
  let regulationEnded = false;
  let postgameReached = false;

  const closeActiveRange = (timestamp: number) => {
    if (activeRange) activeRange.end = timestamp;
    activeRange = undefined;
  };

  for (const [recordIndex, record] of records.entries()) {
    if (record.period === undefined) continue;

    const code = record.period;
    const display = decodePeriodDisplay(code, periodNamingMapping, seasonType);

    if (display.type === 'postgame') {
      closeActiveRange(record.timestamp);
      postgameReached = true;
      continue;
    }

    if (postgameReached) continue;

    const isInterval = display.type === 'interval';
    const startsOvertime = display.type === 'overtime';
    if (regulationEnded && !isInterval && !startsOvertime) continue;
    // A tied game can go Q4 → Break → OT. The break itself must not clear the
    // final-Q4 guard, otherwise a stale 204 record can reopen Q4.
    if (startsOvertime) regulationEnded = false;

    const key = getSegmentKey(code, periodNamingMapping, seasonType);
    if (key === null) {
      closeActiveRange(record.timestamp);
      continue;
    }

    const isFinalRegulationClock =
      display.type === 'period' &&
      ((isQuarterLeague(periodNamingMapping) && display.number === 4) ||
        (isPeriodLeague(periodNamingMapping) && display.number === 3)) &&
      isZeroGameTime(record.gameTime);

    if (isFinalRegulationClock) {
      closeActiveRange(record.timestamp);
      regulationEnded = true;
      continue;
    }

    if (isInterval && regulationEnded && !laterExtraPeriod[recordIndex]) {
      // The final NBA break or hockey Intermission 3 is postgame when no
      // overtime follows. Do not label the extra recorded footage as Break.
      closeActiveRange(record.timestamp);
      postgameReached = true;
      continue;
    }

    if (activeRange?.code === key) {
      activeRange.end = record.timestamp;
      continue;
    }

    closeActiveRange(record.timestamp);
    activeRange = { code: key, start: record.timestamp, end: record.timestamp };
    ranges.push(activeRange);
  }

  return ranges;
};
